/**
 * People — one directory, searchable, filtered by the count you tapped.
 * Route: /team
 * @file src/web/features/team/pages/index.tsx
 *
 * @llm-rule WHEN: A Head or the Company Admin opens the company directory
 * @llm-rule NOTE: ONE LIST. "Today" and "All people" used to be two tabs over
 *   the same humans, each with its own count, so finding someone meant guessing
 *   which tab they were in. Today's state is now a line on the person's row,
 *   and the tiles above filter this one list.
 * @llm-rule NOTE: Every tile counts PEOPLE and filters to exactly those rows,
 *   so a tile can never disagree with the list beneath it.
 * @llm-rule AVOID: Declaring sub-components inside the page function — a
 *   component created during render remounts every keystroke, which steals
 *   focus from the search box.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Button, Skeleton, toast } from '@bloomneo/uikit';
import { ChevronRight, FileSpreadsheet, Plus, Search, X } from 'lucide-react';
import { AuthGuard, useAuth } from '../../auth';
import { AppShell } from '../../../shared/components/AppShell';
import { AddEmployeeDialog } from '../components/AddEmployeeDialog';
import { ImportPeopleDialog } from '../components/ImportPeopleDialog';
import { attendanceDot, Dot, PersonAvatar } from '../../../shared/components/presence';
import { useAppRequest } from '../../../shared/hooks/useAppApi';
import {
  formatClock,
  formatDistance,
  greeting,
  OFFSITE_REASON_LABEL,
  describeRole,
  type HeadDesignation,
  type OrgRole,
} from '../../../shared/domain';

interface RosterRecord {
  id: string;
  clockInAt: string;
  clockOutAt: string | null;
  kind: string;
  approvalStatus: string;
  locationName: string | null;
  distanceM: number | null;
  faceMatchScore: number | null;
  offsiteReason: string | null;
  offsiteNote: string | null;
}

interface RosterPerson {
  id: string;
  attendanceRequired: boolean;
  record: RosterRecord | null;
  onLeave: { type: string; dayPart: string; reason: string } | null;
}

interface DirectoryPerson {
  id: string;
  name: string | null;
  email: string;
  orgRole: OrgRole;
  headDesignation?: HeadDesignation | null;
  title: string | null;
  faceEnrolledAt: string | null;
  managerId: string | null;
  isActive: boolean;
  team: { id: string; name: string } | null;
  department: { id: string; name: string } | null;
}

/** A directory entry with today's attendance stitched on, where there is any. */
interface Row extends DirectoryPerson {
  today: RosterPerson | null;
}

type Bucket = 'all' | 'in' | 'leave' | 'out';

const BUCKET_EMPTY: Record<Bucket, string> = {
  all: 'Nobody here yet.',
  in: 'Nobody has clocked in yet today.',
  leave: 'Nobody is on approved leave today.',
  out: 'Everyone on the clock is either in or on leave.',
};

function inBucket(row: Row, bucket: Bucket): boolean {
  const today = row.today;
  switch (bucket) {
    case 'in':
      return Boolean(today?.record);
    case 'leave':
      return Boolean(today?.onLeave && !today.record);
    // Approved leave is not "yet to clock in", and neither is a rank that is
    // not on the clock at all — counting either as absent makes the tile lie.
    case 'out':
      return Boolean(today && !today.record && !today.onLeave && today.attendanceRequired);
    default:
      return true;
  }
}

function CountTile({
  n,
  label,
  active,
  tone = 'plain',
  onClick,
}: {
  n: number;
  label: string;
  active: boolean;
  tone?: 'plain' | 'good' | 'warn';
  onClick: () => void;
}) {
  const accent: Record<string, string> = {
    plain: 'var(--ink)',
    good: 'var(--dot-onsite)',
    warn: 'var(--dot-offsite)',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="flex min-w-[88px] flex-1 flex-col items-start rounded-[12px] px-3 py-2.5 text-left transition-all active:scale-[0.98]"
      style={{
        border: active ? '2px solid var(--ink)' : '1.5px solid var(--rule)',
        background: active ? 'var(--paper-sunk)' : 'var(--paper)',
      }}
    >
      <span
        className="font-clock text-[20px] leading-none tabular-nums"
        style={{ color: n > 0 ? accent[tone] : 'var(--ink-ghost)' }}
      >
        {n}
      </span>
      <span className="mt-1 text-[11px] leading-tight text-ink-faint">{label}</span>
    </button>
  );
}

/** Today's line for one person — the thing the "Today" tab used to hold. */
function TodayLine({ today }: { today: RosterPerson | null }) {
  if (!today) return <span className="text-ink-ghost">not on the clock</span>;

  const record = today.record;
  if (!record) {
    if (today.onLeave) {
      return (
        <span className="text-offsite">
          on leave — {today.onLeave.dayPart === 'FULL_DAY' ? 'full day' : 'half day'}
        </span>
      );
    }
    return today.attendanceRequired ? (
      <span>not clocked in</span>
    ) : (
      <span className="text-ink-ghost">not on the clock</span>
    );
  }

  return (
    <>
      in {formatClock(record.clockInAt)}
      {record.clockOutAt && ` → out ${formatClock(record.clockOutAt)}`}
      {' · '}
      {record.kind === 'OFF_SITE'
        ? `off-site ${formatDistance(record.distanceM)}${
            record.offsiteReason ? ` · ${OFFSITE_REASON_LABEL[record.offsiteReason]}` : ''
          }`
        : (record.locationName ?? 'on site')}
      {record.faceMatchScore !== null && ` · face ${Math.round(record.faceMatchScore * 100)}%`}
    </>
  );
}

function PersonRow({ row, managerName }: { row: Row; managerName: string | null }) {
  return (
    <li style={{ borderTop: '1.5px dashed var(--rule)' }}>
      <Link
        to={`/team/${row.id}`}
        className="flex items-center gap-3 py-3 transition-colors hover:bg-paper-warm"
      >
        <Dot kind={row.today?.onLeave && !row.today.record ? 'offsite' : attendanceDot(row.today?.record ?? null)} />
        <PersonAvatar person={row} size={34} />

        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className="truncate text-[14px] font-medium text-ink">{row.name}</span>
            {!row.isActive && (
              <span className="label-mono flex-none text-[9.5px] text-ink-ghost">inactive</span>
            )}
          </span>

          <span className="block truncate text-[12px] text-ink-faint">
            {describeRole(row.orgRole, row.headDesignation)}
            {managerName
              ? ` · reports to ${managerName}`
              : row.orgRole === 'COMPANY_ADMIN'
                ? ''
                : ' · reports to nobody yet'}
          </span>

          <span className="block truncate text-[11.5px] text-ink-faint">
            <TodayLine today={row.today} />
          </span>
        </span>

        <ChevronRight className="size-4 flex-none text-ink-ghost" />
      </Link>
    </li>
  );
}

function TeamPage() {
  const { user } = useAuth();
  const { request } = useAppRequest();

  const [roster, setRoster] = useState<RosterPerson[]>([]);
  const [directory, setDirectory] = useState<DirectoryPerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [bucket, setBucket] = useState<Bucket>('all');
  const [query, setQuery] = useState('');

  const role = (user?.orgRole ?? 'EMPLOYEE') as OrgRole;
  /** Only these ranks may open the directory. */
  const isCeoOrCto = role === 'COMPANY_ADMIN' || role === 'HEAD';

  const load = useCallback(async () => {
    const [rosterResult, peopleResult] = await Promise.all([
      request<{ roster: RosterPerson[] }>('/api/attendance/roster'),
      request<{ people: DirectoryPerson[] }>('/api/org/people'),
    ]);

    if (peopleResult.ok) setDirectory(peopleResult.data.people);
    else toast.error(peopleResult.error);

    if (rosterResult.ok) setRoster(rosterResult.data.roster);
    setLoading(false);
  }, [request]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
    The directory is the spine — it holds EVERYONE, including the ranks that are
    not on the clock. The roster covers only the people below this viewer, so it
    is joined on rather than listed separately; that join is what let the two
    tabs collapse into one list.
  */
  const rows = useMemo<Row[]>(() => {
    const byId = new Map(roster.map((r) => [r.id, r]));
    return directory.map((person) => ({ ...person, today: byId.get(person.id) ?? null }));
  }, [directory, roster]);

  const counts = useMemo(
    () => ({
      all: rows.length,
      in: rows.filter((r) => inBucket(r, 'in')).length,
      leave: rows.filter((r) => inBucket(r, 'leave')).length,
      out: rows.filter((r) => inBucket(r, 'out')).length,
    }),
    [rows],
  );

  const nameById = useMemo(
    () => new Map(directory.map((p) => [p.id, p.name])),
    [directory],
  );

  const shown = useMemo(() => {
    const term = query.trim().toLowerCase();
    return rows
      .filter((r) => inBucket(r, bucket))
      .filter(
        (r) =>
          !term ||
          (r.name ?? '').toLowerCase().includes(term) ||
          r.email.toLowerCase().includes(term) ||
          (r.title ?? '').toLowerCase().includes(term),
      );
  }, [rows, bucket, query]);

  const firstName = (user?.name ?? '').split(' ')[0] || 'there';

  // Head and Company Admin only. Hiding the nav entry is not enough — a
  // bookmark would still reach the page, so the route guards itself.
  if (!isCeoOrCto) return <Navigate to="/clock" replace />;

  return (
    <AppShell
      wide
      title="People"
      subtitle={`${greeting()}, ${firstName}. ${counts.in} of ${
        counts.in + counts.out + counts.leave
      } on the clock are in · tap a number to filter, or search for anyone.`}
    >
      <div className="mb-4 flex flex-wrap justify-end gap-2">
        <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}>
          <FileSpreadsheet className="mr-1.5 size-4" />
          Import from Excel
        </Button>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="mr-1.5 size-4" />
          Add employee
        </Button>
      </div>

      {loading ? (
        <Skeleton className="h-96 w-full rounded-[18px]" />
      ) : (
        <div className="flex flex-col">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <CountTile
              n={counts.all}
              label="people"
              active={bucket === 'all'}
              onClick={() => setBucket('all')}
            />
            <CountTile
              n={counts.in}
              label="clocked in"
              tone="good"
              active={bucket === 'in'}
              onClick={() => setBucket('in')}
            />
            <CountTile
              n={counts.out}
              label="yet to clock in"
              tone="warn"
              active={bucket === 'out'}
              onClick={() => setBucket('out')}
            />
            <CountTile
              n={counts.leave}
              label="on leave"
              active={bucket === 'leave'}
              onClick={() => setBucket('leave')}
            />
          </div>

          <div className="mt-3 flex items-center gap-2 rounded-[12px] border border-rule bg-paper px-3 py-2">
            <Search className="size-4 flex-none text-ink-ghost" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, email or title"
              className="min-w-0 flex-1 bg-transparent text-[13.5px] text-ink outline-none placeholder:text-ink-ghost"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                className="grid size-6 flex-none place-items-center rounded-full bg-paper-sunk text-ink-faint"
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>

          <div className="mt-3 flex items-baseline justify-between">
            <span className="label-mono text-ink-faint">
              {bucket === 'all' ? 'Everyone' : 'Filtered'}
            </span>
            <span className="font-clock text-[12px] text-ink-faint">
              {shown.length} of {rows.length}
              {bucket !== 'all' && (
                <button
                  type="button"
                  onClick={() => setBucket('all')}
                  className="ml-2 underline underline-offset-2 hover:text-ink"
                >
                  clear
                </button>
              )}
            </span>
          </div>

          {shown.length === 0 ? (
            <p className="mt-2 rounded-[14px] border border-dashed border-rule px-5 py-10 text-center text-[13px] text-ink-faint">
              {query ? `Nobody matches “${query}”.` : BUCKET_EMPTY[bucket]}
            </p>
          ) : (
            <ul>
              {shown.map((row) => (
                <PersonRow
                  key={row.id}
                  row={row}
                  managerName={row.managerId ? (nameById.get(row.managerId) ?? null) : null}
                />
              ))}
            </ul>
          )}

          <p className="mt-4 pt-3 text-[11.5px] leading-relaxed text-ink-ghost">
            Tap anyone to open their profile — their day, their record, and the actions you hold
            over them. Flags are raised automatically; nobody types them.
          </p>
        </div>
      )}

      <ImportPeopleDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => void load()}
      />

      <AddEmployeeDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        viewerRole={role}
        viewerDepartmentId={user?.departmentId}
        onAdded={() => void load()}
      />
    </AppShell>
  );
}

export default function TeamRoute() {
  return (
    <AuthGuard>
      <TeamPage />
    </AuthGuard>
  );
}
